"use client";
// The author page's article grid: the /blog index card, 12 at a time. Client-side only for
// "Show more" -- the first 12 cards are server-rendered with the page.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CalendarDays } from 'lucide-react';

export interface AuthorArticle {
  id: string;
  href: string;
  title: string;
  excerpt?: string;
  image?: string;
  date?: string;
  readTime?: number;
}

const PAGE = 12;

export function AuthorArticlesGrid({ articles }: { articles: AuthorArticle[] }) {
  const [shown, setShown] = useState(PAGE);
  const visible = articles.slice(0, shown);

  return (
    <>
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {visible.map((a) => (
          <Link key={a.id} to={a.href} className="block group">
            <Card className="h-full overflow-hidden hover:shadow-lg hover:border-primary/50 transition-all">
              {a.image && (
                <div className="overflow-hidden">
                  <img
                    src={a.image}
                    alt={a.title}
                    width={400}
                    height={192}
                    className="h-48 w-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              )}
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                  {a.title}
                </h3>
                {a.excerpt && <p className="text-muted-foreground mb-4 line-clamp-3">{a.excerpt}</p>}
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  {a.date && (
                    <div className="flex items-center gap-2">
                      <CalendarDays className="h-4 w-4" />
                      <span>{a.date}</span>
                    </div>
                  )}
                  {a.readTime ? <span>{a.readTime} min read</span> : null}
                </div>
              </div>
            </Card>
          </Link>
        ))}
      </div>

      {articles.length > shown && (
        <div className="mt-10 flex flex-col items-center gap-2">
          <Button variant="outline" onClick={() => setShown((n) => n + PAGE)}>
            Show more articles
          </Button>
          <span className="text-sm text-muted-foreground">
            Showing {shown} of {articles.length}
          </span>
        </div>
      )}
    </>
  );
}
